import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, api } from "../App";
import { toast } from "sonner";
import {
  ArrowLeft, Users, DollarSign, TrendingUp, UserPlus,
  Gift, BarChart3, GraduationCap, Heart, ShieldCheck,
  ChevronRight, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [giftForm, setGiftForm] = useState({ email: "", tier: "standard", student_limit: 4 });
  const [gifting, setGifting] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [purchases, setPurchases] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    api.get("/admin/stats").then(res => {
      setStats(res.data);
      setLoading(false);
    }).catch(err => {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
        navigate("/dashboard");
      }
      setLoading(false);
    });
  }, [navigate]);

  useEffect(() => {
    if (activeTab === "purchases") {
      api.get("/admin/recent-purchases").then(res => setPurchases(res.data.purchases || [])).catch(() => {});
      api.get("/admin/notifications").then(res => setNotifications(res.data.notifications || [])).catch(() => {});
    }
  }, [activeTab]);

  const handleGift = async () => {
    if (!giftForm.email) { toast.error("Enter an email"); return; }
    setGifting(true);
    try {
      const res = await api.post("/admin/gift-access", giftForm);
      const d = res.data;
      if (d.user_exists) {
        toast.success(`Granted ${giftForm.tier} access to ${giftForm.email}`);
      } else {
        toast.success(`Pre-gifted ${giftForm.tier} to ${giftForm.email} — they'll get it on signup`);
      }
      setGiftForm({ email: "", tier: "standard", student_limit: 4 });
      // Refresh stats
      const statsRes = await api.get("/admin/stats");
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to gift access");
    }
    setGifting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F7931A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = stats ? [
    { label: "Total Users", value: stats.total_users, icon: Users, color: "text-blue-400" },
    { label: "Signups This Week", value: stats.signups_this_week, icon: UserPlus, color: "text-green-400" },
    { label: "Verified Emails", value: stats.verified_users, icon: ShieldCheck, color: "text-emerald-400" },
    { label: "Total Revenue", value: `$${stats.total_revenue_usd?.toLocaleString() || 0}`, icon: DollarSign, color: "text-[#F7931A]" },
    { label: "Transactions", value: stats.total_transactions, icon: TrendingUp, color: "text-purple-400" },
    { label: "Educators", value: stats.total_educators, icon: GraduationCap, color: "text-yellow-400" },
    { label: "Students Linked", value: stats.total_students_linked, icon: Users, color: "text-cyan-400" },
    { label: "Families Funded", value: stats.fund_family_funded, icon: Heart, color: "text-pink-400" },
  ] : [];

  const tabs = [
    { id: "overview", label: "Overview", icon: BarChart3 },
    { id: "purchases", label: "Purchases", icon: DollarSign },
    { id: "gift", label: "Gift Access", icon: Gift },
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/dashboard")} className="text-zinc-400 hover:text-white" data-testid="admin-back-btn">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">ADMIN <span className="text-[#F7931A]">DASHBOARD</span></h1>
          <div className="w-5" />
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-5xl mx-auto px-4 mt-6">
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? "bg-[#F7931A]/20 text-[#F7931A] border border-[#F7931A]/30"
                  : "text-zinc-400 hover:text-white border border-white/5"
              }`}
              data-testid={`admin-tab-${tab.id}`}>
              <tab.icon size={16} /> {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === "overview" && stats && (
        <div className="max-w-5xl mx-auto px-4 pb-20">
          {/* Stat Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
            {statCards.map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4"
                data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
                <s.icon size={20} className={`${s.color} mb-2`} />
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-zinc-500 mt-1">{s.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Tier Breakdown */}
          <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6 mb-6" data-testid="admin-tier-breakdown">
            <h3 className="font-heading text-lg font-bold text-white mb-4">User Tiers</h3>
            <div className="space-y-3">
              {Object.entries(stats.tier_breakdown || {}).map(([tier, count]) => {
                const pct = stats.total_users > 0 ? Math.round((count / stats.total_users) * 100) : 0;
                return (
                  <div key={tier} className="flex items-center gap-3">
                    <span className="text-sm text-zinc-400 w-28 capitalize">{tier || "free"}</span>
                    <div className="flex-1 bg-[#161616] rounded-full h-6 overflow-hidden">
                      <div className="h-full bg-[#F7931A]/80 rounded-full flex items-center px-2"
                        style={{ width: `${Math.max(pct, 5)}%` }}>
                        <span className="text-xs font-bold text-black">{count}</span>
                      </div>
                    </div>
                    <span className="text-xs text-zinc-500 w-10 text-right">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Fund a Family */}
          {stats.fund_family_pending > 0 && (
            <button onClick={() => navigate("/admin/fund-a-family")}
              className="w-full bg-[#0A0A0A] border border-[#F7931A]/30 rounded-xl p-4 flex items-center justify-between hover:border-[#F7931A]/60 transition-all"
              data-testid="admin-pending-applications">
              <div className="flex items-center gap-3">
                <Heart className="text-pink-400" size={20} />
                <span className="text-white font-medium">{stats.fund_family_pending} pending Fund a Family applications</span>
              </div>
              <ChevronRight className="text-zinc-500" size={16} />
            </button>
          )}
        </div>
      )}

      {activeTab === "purchases" && (
        <div className="max-w-5xl mx-auto px-4 pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            {/* Notifications (webhook-triggered) */}
            {notifications.length > 0 && (
              <div className="mb-6" data-testid="admin-purchase-notifications">
                <h3 className="font-heading text-lg font-bold text-white mb-4">Purchase Notifications</h3>
                <div className="space-y-3">
                  {notifications.map((n, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 flex items-center gap-4"
                      data-testid={`admin-notification-${i}`}>
                      <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                        <DollarSign size={18} className="text-green-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">{n.buyer_email}</p>
                        <p className="text-xs text-zinc-500">
                          <span className="text-[#F7931A] capitalize">{(n.tier || "").replace("_", " ")}</span>
                          {" — "}
                          <span className="text-green-400">${n.amount_usd}</span>
                        </p>
                      </div>
                      <span className="text-xs text-zinc-600 flex-shrink-0">
                        {n.created_at ? new Date(n.created_at).toLocaleDateString() : ""}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All paid transactions */}
            <div data-testid="admin-recent-purchases">
              <h3 className="font-heading text-lg font-bold text-white mb-4">All Paid Transactions</h3>
              {purchases.length === 0 ? (
                <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-8 text-center">
                  <DollarSign size={32} className="text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-500">No purchases yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {purchases.map((p, i) => (
                    <div key={i} className="bg-[#0A0A0A] border border-white/5 rounded-xl p-4 flex items-center gap-4"
                      data-testid={`admin-purchase-${i}`}>
                      <div className="w-10 h-10 rounded-full bg-[#F7931A]/10 flex items-center justify-center flex-shrink-0">
                        <TrendingUp size={18} className="text-[#F7931A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">{p.user_email}</p>
                        <p className="text-xs text-zinc-500">
                          <span className="text-[#F7931A] capitalize">{(p.tier || "").replace("_", " ")}</span>
                          {p.student_limit > 0 && <span className="text-zinc-600"> ({p.student_limit} students)</span>}
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm text-green-400 font-bold">${p.amount}</p>
                        <p className="text-xs text-zinc-600">
                          {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : p.created_at ? new Date(p.created_at).toLocaleDateString() : ""}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {activeTab === "gift" && (
        <div className="max-w-xl mx-auto px-4 pb-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-[#0A0A0A] border border-white/5 rounded-xl p-6"
            data-testid="admin-gift-access-form">
            <div className="flex items-center gap-3 mb-6">
              <Gift className="text-[#F7931A]" size={24} />
              <div>
                <h3 className="font-heading text-lg font-bold text-white">Gift Access</h3>
                <p className="text-sm text-zinc-500">Grant any email full access — for reviewers, press, or friends</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-1">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={16} />
                  <input type="email" placeholder="reviewer@example.com"
                    value={giftForm.email} onChange={e => setGiftForm(f => ({...f, email: e.target.value}))}
                    className="w-full bg-[#161616] border border-white/10 rounded-lg px-10 py-3 text-white placeholder-zinc-600 focus:border-[#F7931A]/50 outline-none"
                    data-testid="gift-email-input" />
                </div>
              </div>

              <div>
                <label className="block text-sm text-zinc-400 mb-1">Tier</label>
                <select value={giftForm.tier} onChange={e => setGiftForm(f => ({...f, tier: e.target.value}))}
                  className="w-full bg-[#161616] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#F7931A]/50 outline-none"
                  data-testid="gift-tier-select">
                  <option value="genesis">Genesis ($21 value)</option>
                  <option value="early_believer">Early Believer ($42 value)</option>
                  <option value="standard">Standard ($49 value)</option>
                  <option value="educator">Educator License</option>
                </select>
              </div>

              {giftForm.tier === "educator" && (
                <div>
                  <label className="block text-sm text-zinc-400 mb-1">Student Limit</label>
                  <select value={giftForm.student_limit} onChange={e => setGiftForm(f => ({...f, student_limit: parseInt(e.target.value)}))}
                    className="w-full bg-[#161616] border border-white/10 rounded-lg px-4 py-3 text-white focus:border-[#F7931A]/50 outline-none"
                    data-testid="gift-student-limit-select">
                    <option value={4}>4 Students</option>
                    <option value={8}>8 Students</option>
                    <option value={12}>12 Students</option>
                    <option value={999}>Unlimited</option>
                  </select>
                </div>
              )}

              <Button onClick={handleGift} disabled={gifting}
                className="w-full bg-[#F7931A] hover:bg-[#F7931A]/90 text-black font-bold py-3"
                data-testid="gift-access-btn">
                {gifting ? "Granting..." : "Grant Access"}
              </Button>

              <p className="text-xs text-zinc-600 text-center">
                If the user hasn't registered yet, they'll receive this tier automatically when they sign up.
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
